export type CustomDataRow = {
  field_index?: number | null;
  field_value?: string | null;
};

/**
 * Builds a dense custom_data array where slot N maps to field_index N.
 */
export const densifyCustomData = <T extends CustomDataRow>(
  customDataArray: T[] | undefined | null,
  maxIndex?: number,
): T[] => {
  if (!customDataArray?.length && maxIndex === undefined) {
    return [];
  }

  const byIndex = new Map<number, T>();

  customDataArray?.forEach((row, fallbackIndex) => {
    const parsedIndex =
      row.field_index !== undefined &&
      row.field_index !== null &&
      Number.isFinite(Number(row.field_index))
        ? Number(row.field_index)
        : fallbackIndex;

    if (parsedIndex >= 0) {
      byIndex.set(parsedIndex, row);
    }
  });

  const inferredMax = Math.max(-1, ...byIndex.keys());
  const targetMax = Math.max(inferredMax, maxIndex ?? -1);

  if (targetMax < 0) {
    return [];
  }

  const out: T[] = [];

  for (let i = 0; i <= targetMax; i++) {
    const existing = byIndex.get(i);

    out[i] = {
      ...(existing ?? {}),
      field_index: i,
      field_value: existing?.field_value ?? null,
    } as T;
  }

  return out;
};

/**
 * Prepares custom_data rows for API save by normalizing indices and dropping empties.
 */
export const prepareCustomDataForSave = <T extends CustomDataRow>(customDataArray: T[] | undefined | null): T[] => {
  if (!customDataArray?.length) {
    return [];
  }

  const dense = densifyCustomData(customDataArray);

  return dense.reduce<T[]>((acc, row, i) => {
    const fieldValue = typeof row.field_value === "string" ? row.field_value.trim() : row.field_value;

    if (fieldValue === "" || fieldValue === null || fieldValue === undefined) {
      return acc;
    }

    acc.push({
      ...row,
      field_index: i,
      field_value: fieldValue,
    } as T);

    return acc;
  }, []);
};

/**
 * Backward-compatible in-place normalizer used by existing forms.
 */
export const setCustomDataArray = <T extends CustomDataRow>(
  customDataArray: T[] | undefined | null,
  maxIndex?: number,
): void => {
  if (!customDataArray) {
    return;
  }

  const dense = densifyCustomData(customDataArray, maxIndex);
  customDataArray.splice(0, customDataArray.length, ...dense);
};
